import { useCallback, useRef, useState } from 'react';
import { getCartCode } from '../../../utils/cartStorage';
import {
  checkoutPlaceOrderFromCart,
  requestCouponApply,
  requestPricesByPaymentMethod,
  processAdditionalPayment,
  stripeElementsFinalize,
  chargeUpsell,
} from '../../../services/checkout/checkout.service';
import { CheckoutError, type CheckoutResponseDto } from '../../../services/checkout/checkout.types';
import type { FormData, PaymentInformationData, FinalPaymentData } from '../../../types';
import type { StepperStep } from '../../../types';
import { useErrorAlerts } from '../../../contexts/ErrorContext';
import type { ShopifyOfferDto, OneClickUpsell } from '../../../offer.types';
import { getOfferSubtotal, getOfferTotal, getOfferDiscount } from '../../../utils/pricing';
import { useLatam3DS } from '../../../services/card3ds';

declare global {
  interface Window {
    __pagouSubmitToken?: () => Promise<string>;
    __pagouResetToken?: () => void;
    __pagouCardReady?: boolean;
    setCheckoutPaymentErrorMessage?: (message: string) => void;
  }
}

/**
 * Exibe uma mensagem de recusa/erro no overlay do checkout (CheckoutLoading) —
 * o único renderizador de erro visível. O ErrorContext.addError continua sendo
 * no-op visual (P2-3), então sem essa ponte o cliente não vê nada. Centralizado
 * aqui para evitar duplicar o try/catch em cada ramo de falha (card_denied, 3DS).
 */
const showCheckoutPaymentError = (message: string): void => {
  try {
    if (typeof window !== 'undefined' && window.setCheckoutPaymentErrorMessage) {
      window.setCheckoutPaymentErrorMessage(message);
    }
  } catch {
    // setter ainda não montado — addError abaixo mantém o erro no contexto
  }
};

export const useCheckoutPayment = (
  formData: FormData,
  shopifyOffer: ShopifyOfferDto | null,
  steps: StepperStep[],
  setFormData: (updater: (prev: FormData) => FormData) => void,
  setOrderResponse: (response: CheckoutResponseDto | null) => void,
  setEditingSection: (section: any) => void,
  addLoading: (state: string) => void,
  removeLoading: (state: string) => void,
  trackAddShippingInfo: () => void,
  trackAddPaymentInfo: (paymentInfo: PaymentInformationData) => void,
  trackPurchase: (orderResponse: CheckoutResponseDto) => void,
  _checkoutSteps: StepperStep[],
  createOrUpdateCartData?: (userData: any) => Promise<any>,
  concludeCart?: () => Promise<void>,
) => {
  const errorsContext = useErrorAlerts();
  const lastStepCompleteRef = useRef<string>('');
  const stripeCardHandlersRef = useRef<{ getCardElement: () => any; getStripe: () => unknown } | null>(null);
  // Token reutilizável (`pgpm_`) capturado pelo Payment Element da Pagou quando há upsell.
  // Retido em memória durante a sessão do checkout para reuso na cobrança do upsell.
  const reusableTokenRef = useRef<string | null>(null);

  // Estado do popup de upsell one-click. Quando preenchido, CheckoutPage renderiza o popup
  // e o redirect pro thank-you fica retido até o cliente decidir.
  const [upsellState, setUpsellState] = useState<{
    upsell: OneClickUpsell;
    orderId: string;
    reusableToken: string;
    checkoutToken?: string;
  } | null>(null);

  // O Payment Element da Pagou reporta o token reutilizável `pgpm_` por aqui.
  const onReusableToken = useCallback((token: string) => {
    const normalized = String(token || '').trim();
    reusableTokenRef.current = normalized.startsWith('pgpm_') ? normalized : null;
  }, []);

  const onCardElementReady = useCallback((handlers: { getCardElement: () => any; getStripe: () => unknown }) => {
    stripeCardHandlersRef.current = handlers;
  }, [shopifyOffer]);

  // Hook 3DS Latam - carregado apenas quando necessário
  const { executeFull3DSFlow } = useLatam3DS(false); // Não carrega por padrão

  const redirectToThankYou = useCallback((checkoutToken?: string) => {
    const token = String(checkoutToken || '').trim();
    if (!token || typeof window === 'undefined') return;
    const targetPath = `/checkout/thank-you/${encodeURIComponent(token)}`;
    if (window.location.pathname !== targetPath) {
      window.location.href = targetPath;
    }
  }, []);

  /**
   * Gancho único de "cartão aprovado". Chamado nos 3 pontos onde o cartão é confirmado.
   * Se houver upsell one-click + método cartão + token reutilizável `pgpm_` capturado,
   * ABRE o popup de upsell e SEGURA o redirect até o cliente decidir. Caso contrário,
   * redireciona direto pro thank-you (comportamento atual).
   */
  const onCardApproved = useCallback(
    (checkoutResponse: CheckoutResponseDto, checkoutToken?: string) => {
      const upsell = shopifyOffer?.oneClickUpsell ?? null;
      const isCreditCard = checkoutResponse.paymentMethod?.paymentMethod === 'credit_card';
      const reusableToken = reusableTokenRef.current;
      const orderId = String(checkoutResponse.order?.id ?? '').trim();

      if (upsell && isCreditCard && reusableToken && orderId && checkoutToken) {
        // Segura o redirect: o popup decide quando navegar (accept/decline/Esc).
        // checkoutToken é obrigatório — o backend amarra a cobrança a ele.
        setUpsellState({
          upsell,
          orderId,
          reusableToken,
          checkoutToken,
        });
        return;
      }

      redirectToThankYou(checkoutToken);
    },
    [shopifyOffer, redirectToThankYou],
  );

  /**
   * ✅ NOVA FUNÇÃO: Captura dados de pagamento diretamente do formulário
   * Sem depender do formData.paymentInformation obsoleto
   */
  const capturePaymentDataFromForm = useCallback(async (): Promise<FinalPaymentData | null> => {
    try {
      // Busca o formulário de pagamento ativo
      const paymentForm = document.querySelector('[data-payment-form]') as HTMLFormElement;

      if (!paymentForm) {
        // Tentar outros seletores como fallback
        const alternativeForm = document.querySelector('form') as HTMLFormElement;

        if (!alternativeForm) {
          return null;
        }
      }

      // Captura dados do formulário
      const formData = new FormData(paymentForm || (document.querySelector('form') as HTMLFormElement));

      const paymentMethod = formData.get('paymentMethod') as string;

      if (!paymentMethod) {
        // Tentar detectar método baseado nos campos preenchidos
        if (!formData.get('cardNumber') && !formData.get('cpf')) {
          return null;
        }
      }

      // Constrói dados baseado no método selecionado
      const finalPaymentData: FinalPaymentData = {
        paymentMethod: paymentMethod as any,
      };

      if (paymentMethod === 'credit_card') {
        const paymentCfg = (shopifyOffer as any)?.payment || {};
        const gatewayPagouConfigured =
          String(paymentCfg.creditCardPrimaryGateway || '').toLowerCase() === 'pagou';
        const gatewayPagarmeConfigured =
          String(paymentCfg.creditCardPrimaryGateway || '').toLowerCase() === 'pagarme';
        const hasPagouPublicKey = Boolean(String(paymentCfg.pagouPublicKey || '').trim());
        const pagarmePublicKey = String(paymentCfg.pagarmePublicKey || '').trim();
        let cardNumberValue = (formData.get('cardNumber') as string) || '';
        let expirationDateValue = (formData.get('expirationDate') as string) || '';
        let cvvValue = (formData.get('cvv') as string) || '';
        const cardholderNameValue = (formData.get('cardholderName') as string) || '';

        // Payment Element registra __pagouSubmitToken ao montar. Aguarda ready (race de remount/recalc).
        if (gatewayPagouConfigured) {
          if (!hasPagouPublicKey) {
            throw new Error(
              'Public Key Pagou ausente na oferta. Configure a chave pública no admin (Integrações → Pagou).',
            );
          }

          const readyDeadline = Date.now() + 5000;
          while (
            Date.now() < readyDeadline &&
            !(typeof window.__pagouSubmitToken === 'function' && window.__pagouCardReady === true)
          ) {
            await new Promise((r) => setTimeout(r, 100));
          }

          if (typeof window.__pagouSubmitToken !== 'function') {
            throw new Error(
              'Campo do cartão Pagou ainda não carregou. Aguarde "Carregando checkout seguro" ou recarregue a página.',
            );
          }
          if (window.__pagouCardReady !== true) {
            throw new Error(
              'Campo do cartão Pagou ainda não carregou. Aguarde "Carregando checkout seguro" ou recarregue a página.',
            );
          }

          cardNumberValue = await window.__pagouSubmitToken!();
          // Token reutilizável (upsell): retém para reuso na cobrança do upsell one-click.
          if (String(cardNumberValue).startsWith('pgpm_')) {
            reusableTokenRef.current = cardNumberValue;
          }
        }

        // Pagar.me: tokeniza no client (POST /tokens?appId=pk_) — backend só recebe token_*.
        if (gatewayPagarmeConfigured) {
          if (!pagarmePublicKey) {
            throw new Error(
              'Public Key Pagar.me ausente na oferta. Configure a chave pública no admin (Integrações → Pagar.me).',
            );
          }
          const { tokenizePagarmeCard, parseCardExpiry } = await import(
            '../../../services/pagarme/tokenizeCard'
          );
          const { expMonth, expYear } = parseCardExpiry(expirationDateValue);
          cardNumberValue = await tokenizePagarmeCard({
            publicKey: pagarmePublicKey,
            number: cardNumberValue,
            holderName: cardholderNameValue,
            expMonth,
            expYear,
            cvv: cvvValue,
          });
          // Não reenviar PAN/CVV — placeholders só para satisfazer o DTO unificado.
          expirationDateValue = '12/99';
          cvvValue = '000';
        }

        const isValidPagouToken =
          String(cardNumberValue).startsWith('pgct_') || String(cardNumberValue).startsWith('pgpm_');
        if (gatewayPagouConfigured && !isValidPagouToken) {
          throw new Error('Token Pagou inválido/ausente. Não foi possível tokenizar o cartão.');
        }
        if (gatewayPagarmeConfigured && !String(cardNumberValue).startsWith('token_')) {
          throw new Error('Token Pagar.me inválido/ausente. Não foi possível tokenizar o cartão.');
        }

        finalPaymentData.creditCard = {
          cardNumber: cardNumberValue,
          expirationDate: expirationDateValue,
          cvv: cvvValue,
          cardholderName: cardholderNameValue,
          cpf: (formData.get('cpf') as string) || '',
          installments: (formData.get('installments') as string) || '1',
        };
      } else if (paymentMethod === 'pix') {
        finalPaymentData.pix = {
          cpf: (formData.get('cpf') as string) || '',
        };
      } else if (paymentMethod === 'boleto') {
        finalPaymentData.boleto = {
          cpf: (formData.get('cpf') as string) || '',
        };
      }

      return finalPaymentData;
    } catch (error) {
      console.error('❌ Erro ao capturar dados de pagamento:', error);
      return null;
    }
  }, [shopifyOffer]);

  /**
   * ✅ Função genérica para recalcular preços via backend
   * Backend processa tudo: cupons + desconto método + breakdown
   */
  const updatePricesFromBackend = useCallback(
    async (loadingKey: string = 'updatePrices', customCoupons?: string[], customPaymentMethod?: string) => {
      if (!shopifyOffer?.nanoId) return;

      // ✅ Se não houver método de pagamento selecionado, usar PIX como padrão para recálculo
      const paymentMethodToUse = customPaymentMethod || formData.paymentInformation.paymentMethod || 'PIX';

      try {
        addLoading(loadingKey);

        // ✅ Usa cupons customizados ou do formData
        const couponsToUse = customCoupons || formData.orderSummary.coupons?.map((c) => c.code) || [];

        // ✅ Usa offerNanoId se disponível (carrinho recuperado) ou nanoId (oferta normal)
        const offerUuid = (shopifyOffer as any).offerNanoId || shopifyOffer.nanoId;

        // ✅ cartCode (se existir) entra na query pro backend somar order bumps aceitos
        const cartCodeForPricing = getCartCode() ?? undefined;

        // ✅ Backend faz todo o processamento
        const updatedOffer = await requestPricesByPaymentMethod(
          offerUuid,
          paymentMethodToUse,
          couponsToUse,
          cartCodeForPricing,
        );

        // ✅ Frontend apenas aplica os valores calculados pelo backend (pricing canônico)
        const finalPrice = getOfferTotal(updatedOffer);
        const totalDiscount = getOfferDiscount(updatedOffer);

        setFormData((prev) => {
          // Preserva chaves públicas de gateway se o recalc de preço omitir (anti-desmontagem Pagou/Stripe).
          const prevPayment = (prev.shopifyOffer as any)?.payment || {};
          const nextPayment = { ...((updatedOffer as any)?.payment || {}) };
          for (const key of [
            'creditCardPrimaryGateway',
            'creditCardGatewayRequires3DS',
            'pagouPublicKey',
            'pagouProduction',
            'stripePublishableKey',
            'mercadoPagoPublicKey',
            'wallets',
          ]) {
            if (nextPayment[key] == null && prevPayment[key] != null) {
              nextPayment[key] = prevPayment[key];
            }
          }
          const mergedOffer = { ...updatedOffer, payment: nextPayment } as typeof updatedOffer;

          return {
            ...prev,
            orderSummary: {
              ...prev.orderSummary,
              subtotal: getOfferSubtotal(mergedOffer),
              total: finalPrice,
              discountAmount: totalDiscount,
              // ✅ Preservar cupons que já estão no orderSummary (aplicados manualmente ou removidos)
              // Não sobrescrever se já foram definidos
              ...(prev.orderSummary?.coupons !== undefined && {
                coupons: prev.orderSummary.coupons,
                couponCode: prev.orderSummary.couponCode,
              }),
            },
            shopifyOffer: mergedOffer,
          };
        });
      } catch (error) {
        console.error('Erro ao atualizar preços:', error);
        errorsContext.addError(new CheckoutError('Erro ao calcular preços'), { severity: 'warning' });
      } finally {
        removeLoading(loadingKey);
      }
    },
    [shopifyOffer, formData, setFormData, addLoading, removeLoading, errorsContext],
  );

  const handleCouponApply = useCallback(
    async (couponCode: string) => {
      if (!couponCode || couponCode.trim() === '') {
        // ✅ Remove cupons e recalcula via backend
        setFormData((prev) => ({
          ...prev,
          orderSummary: {
            ...prev.orderSummary,
            couponCode: '',
            coupons: [],
            discountAmount: 0,
          },
        }));

        // ✅ Recalcula preços sem cupons via backend
        await updatePricesFromBackend('removeCoupon', []);

        // ✅ ATUALIZA O CARRINHO NO BANCO REMOVENDO OS CUPONS
        try {
          const cartCode = getCartCode();
          if (cartCode) {
            const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/carts/${cartCode}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                appliedCoupons: [],
                totalDiscounts: 0,
              }),
            });

            if (!response.ok) {
              console.error('❌ Erro ao remover cupons do carrinho:', response.status, response.statusText);
            }
          }
        } catch (cartError) {
          console.error('Erro ao atualizar carrinho:', cartError);
        }

        return;
      }

      addLoading('applyCoupon');

      try {
        const applyCouponResponse = await requestCouponApply(couponCode, formData);

        const discountAmount =
          (Number(applyCouponResponse.discountAboutTotalValue) || 0) +
          (Number(applyCouponResponse.discountAboutProducts) || 0) +
          (Number(applyCouponResponse.discountAboutFreight) || 0);

        // ✅ Prepara lista atualizada de cupons
        const updatedCoupons = [...applyCouponResponse.coupons, ...(formData.orderSummary.coupons ?? [])];
        const couponCodes = updatedCoupons.map((c) => c.code);

        setFormData((prev) => ({
          ...prev,
          orderSummary: {
            ...prev.orderSummary,
            couponCode,
            coupons: updatedCoupons,
            discountAmount,
            // Usar valores do backend
            subtotal: Number(prev.shopifyOffer.original_price),
            total: Number(prev.shopifyOffer.original_price) - discountAmount,
          },
        }));

        // ✅ Recalcula preços com cupons corretos via backend
        await updatePricesFromBackend('applyCoupon', couponCodes);

        // ✅ ATUALIZA O CARRINHO NO BANCO COM OS NOVOS CUPONS
        try {
          const cartCode = getCartCode();
          if (cartCode) {
            const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/carts/${cartCode}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                appliedCoupons: updatedCoupons.map((coupon) => ({
                  code: coupon.code,
                  discount: coupon.discountValue,
                  type: coupon.discountType,
                })),
                totalDiscounts: discountAmount,
              }),
            });

            if (response.ok) {
              const updatedCart = await response.json();
              console.log('✅ Carrinho atualizado no banco com sucesso:', updatedCart);
            } else {
              console.error('❌ Erro ao atualizar carrinho:', response.status, response.statusText);
            }
          }
        } catch (cartError) {
          console.error('Erro ao atualizar carrinho:', cartError);
        }
      } catch (error) {
        console.error('Erro ao aplicar cupom ao pedido:', error);
        throw error;
      } finally {
        removeLoading('applyCoupon');
      }
    },
    [setFormData, addLoading, removeLoading, formData, updatePricesFromBackend],
  );

  const handlePaymentMethodChange = useCallback(
    async (paymentInformation: PaymentInformationData) => {
      // Atualiza dados de pagamento
      setFormData((prev) => ({ ...prev, paymentInformation }));

      // Recalcula preços e envia evento (se método válido)
      if (paymentInformation.paymentMethod && paymentInformation.paymentMethod.trim() !== '') {
        // ✅ Passa o método correto diretamente
        await updatePricesFromBackend('paymentMethodChange', undefined, paymentInformation.paymentMethod);
        trackAddPaymentInfo(paymentInformation);
      }
    },
    [setFormData, trackAddPaymentInfo, updatePricesFromBackend],
  );

  const handlePlaceOrder = useCallback(async () => {
    // ✅ LOG BACKEND: Usuário clicou em "Finalizar Pedido"
              console.log('Usuário clicou em "Finalizar Pedido" - iniciando processamento');
    
    // ✅ LOG BACKEND: Verificando se já está processando
    if (typeof window !== 'undefined' && (window as any).logToBackend) {
      (window as any).logToBackend('Verificando estado antes de processar pedido', 'loading');
    }
    
    // ✅ Removida validação prévia - o formulário já valida e mostra mensagens específicas
    addLoading('placeOrder');

    try {
      // ✅ NOVO SISTEMA: Capturar dados de pagamento diretamente do formulário
      const capturedPaymentData = await capturePaymentDataFromForm();
      if (!capturedPaymentData) {
        errorsContext.addError(new CheckoutError('Erro ao capturar dados de pagamento. Verifique o formulário e tente novamente.'));
        removeLoading('placeOrder');
        return;
      }

      // ✅ ALTERAÇÃO MÍNIMA: Buscar dados do carrinho ao invés de usar formData
      const cartCode = getCartCode();
      if (!cartCode) {
        errorsContext.addError(new CheckoutError('Carrinho não encontrado. Tente recarregar a página.'));
        removeLoading('placeOrder');
        return;
      }

      // ✅ Buscar dados do carrinho
      const cartResponse = await fetch(`${import.meta.env.VITE_BACKEND_URL}/carts/${cartCode}`);
      if (!cartResponse.ok) {
        errorsContext.addError(new CheckoutError('Erro ao buscar dados do carrinho. Tente novamente.'));
        removeLoading('placeOrder');
        return;
      }

      const cartData = await cartResponse.json();
      
      // ✅ LOG BACKEND: Dados do carrinho obtidos
      if (typeof window !== 'undefined' && (window as any).logToBackend) {
        (window as any).logToBackend(`Dados do carrinho obtidos: ${JSON.stringify({
          cartCode,
          totalPrice: cartData.totalPrice,
          totalDiscounts: cartData.totalDiscounts,
          itemsCount: cartData.items?.length || 0,
          offerNanoId: cartData.offerNanoId,
          customerName: cartData.customerData?.name?.substring(0, 3) + '***',
          customerEmail: cartData.customerData?.email?.substring(0, 3) + '***'
        })}`, 'success');
      }

      // ✅ DEBUG: Verificar dados brutos do carrinho
      console.log('🔍 DEBUG - Dados brutos do carrinho:', {
        cartCode,
        cartData,
        items: cartData.items,
        firstItem: cartData.items?.[0],
        itemStructure: cartData.items?.[0] ? Object.keys(cartData.items[0]) : [],
        offerId: cartData.offerId,
        offerNanoId: cartData.offerNanoId,
      });

      // ✅ DEBUG: Verificar se os dados estão corrompidos
      if (cartData.items?.[0]) {
        const item = cartData.items[0];
        console.log('🔍 DEBUG - Análise do primeiro item:', {
          productId: item.productId,
          productIdType: typeof item.productId,
          productIdValid: item.productId > 0,
          name: item.name,
          nameType: typeof item.name,
          nameValid: item.name && item.name.trim().length > 0,
          quantity: item.quantity,
          price: item.price,
          image: item.image,
        });
      }

      // ✅ Criar formData a partir do carrinho + dados de pagamento CAPTURADOS
      const cartFormData = {
        userIdentification: {
          fullName: cartData.customerData.name,
          email: cartData.customerData.email,
          phone: cartData.customerData.phone,
          cpf: cartData.customerData.document || '',
          // ✅ CORREÇÃO: Converter birth de ISO (YYYY-MM-DD) para DD/MM/YYYY
          birth: cartData.customerData.birth ? cartData.customerData.birth.split('-').reverse().join('/') : '',
          cpfOnIdentification: cartData.customerData.document ? true : false, // ✅ Do carrinho
        },
        shippingAddress: {
          zipCode: cartData.customerData.address.postcode,
          street: cartData.customerData.address.street,
          number: cartData.customerData.address.streetNumber,
          complement: cartData.customerData.address.streetComplement,
          neighborhood: cartData.customerData.address.streetDistrict,
          city: cartData.customerData.address.city,
          state: cartData.customerData.address.state,
        },
        paymentInformation: {
          // ✅ NOVO SISTEMA: Usar dados capturados diretamente do formulário
          paymentMethod: capturedPaymentData.paymentMethod,
          creditCard: capturedPaymentData.creditCard,
          pix: capturedPaymentData.pix,
          boleto: capturedPaymentData.boleto,
        },
        orderSummary: {
          total: cartData.totalPrice,
          discountAmount: cartData.totalDiscounts,
          coupons: cartData.appliedCoupons || [],
          products: cartData.items || [],
          subtotal: cartData.totalPrice + (cartData.totalDiscounts || 0),
          shipping: 0, // Se necessário, buscar do carrinho
        },
        shopifyOffer: {
          nanoId: cartData.offerNanoId,
          uuid: cartData.offerNanoId, // Fallback se não tiver uuid
          cart: { metadata: { line_items: cartData.items } }, // Estrutura necessária
          original_price: cartData.totalPrice + (cartData.totalDiscounts || 0),
          price: cartData.totalPrice,
        },
      } as FormData;

      // ✅ CORREÇÃO: Adicionar variant_id que o backend espera
      if (cartFormData.orderSummary.products && cartFormData.orderSummary.products.length > 0) {
        cartFormData.orderSummary.products = cartFormData.orderSummary.products.map((item) => ({
          ...item,
          variant_id: (item as any).productId || item.id, // ✅ Adiciona variant_id baseado no productId ou id
        }));
      }

      // ✅ CORREÇÃO: Também corrigir os items principais que o backend valida
      if (cartFormData.orderSummary.products) {
        cartFormData.orderSummary.products = cartFormData.orderSummary.products.map((item) => ({
          ...item,
          variant_id: Number((item as any).productId || item.id), // ✅ Garante que variant_id seja number
          name: (item as any).name || item.name || 'Produto', // ✅ Garante que name seja string válida
        }));
      }

      // ✅ LOG BACKEND: Dados preparados para envio
      if (typeof window !== 'undefined' && (window as any).logToBackend) {
        (window as any).logToBackend(`Dados preparados para envio: ${JSON.stringify({
          paymentMethod: cartFormData.paymentInformation.paymentMethod,
          cardBrand: cartFormData.paymentInformation.creditCard?.cardNumber?.substring(0, 4) + '****',
          expirationDate: cartFormData.paymentInformation.creditCard?.expirationDate,
          cvv: cartFormData.paymentInformation.creditCard?.cvv?.substring(0, 1) + '**',
          holderName: cartFormData.paymentInformation.creditCard?.cardholderName?.substring(0, 3) + '***',
          installments: cartFormData.paymentInformation.creditCard?.installments,
          customerName: cartFormData.userIdentification.fullName?.substring(0, 3) + '***',
          customerEmail: cartFormData.userIdentification.email?.substring(0, 3) + '***',
          customerCPF: cartFormData.userIdentification.cpf?.substring(0, 3) + '***',
          birth: cartFormData.userIdentification.birth,
          addressCity: cartFormData.shippingAddress.city,
          addressState: cartFormData.shippingAddress.state
        })}`, 'loading');
      }
      
      // ✅ DEBUG: Verificar dados que estão sendo enviados
      console.log('🔍 DEBUG - Dados sendo enviados para o backend:', {
        paymentMethod: cartFormData.paymentInformation.paymentMethod,
        creditCard: cartFormData.paymentInformation.creditCard,
        birth: cartFormData.userIdentification.birth,
        birthType: typeof cartFormData.userIdentification.birth,
        birthLength: cartFormData.userIdentification.birth?.length,
      });

      // ✅ DEBUG: Verificar dados dos itens (PROBLEMA IDENTIFICADO)
      console.log('🔍 DEBUG - Dados dos itens sendo enviados:', {
        items: cartFormData.orderSummary.products,
        itemsCount: cartFormData.orderSummary.products?.length,
        firstItem: cartFormData.orderSummary.products?.[0],
        cartItems: cartData.items,
      });

      // ✅ LOG BACKEND: Enviando requisição para backend
      if (typeof window !== 'undefined' && (window as any).logToBackend) {
        (window as any).logToBackend(`Enviando requisição para /checkout/unified - cartCode: ${cartCode}`, 'loading');
      }
      
      const checkoutPromise = checkoutPlaceOrderFromCart(cartCode, capturedPaymentData);

      // ✅ CORREÇÃO: Usar função que processa dados do carrinho ao invés de FormData
      checkoutPromise
        .then((unifiedResponse) => {
          // ✅ LOG: Resposta recebida do backend (apenas console local)
          console.log('[CHECKOUT][DEBUG] Resposta completa do backend:', unifiedResponse);
          console.log('Resposta recebida do backend:', {
            success: unifiedResponse.success,
            orderId: unifiedResponse.orderId,
            transactionId: unifiedResponse.transactionId,
            gatewayUsed: (unifiedResponse as any).gatewayUsed,
            error: unifiedResponse.error,
            message: (unifiedResponse as any).message,
            paymentMethod: cartFormData.paymentInformation.paymentMethod,
            hasPaymentData: !!unifiedResponse.paymentData,
            hasDetails: !!unifiedResponse.paymentData?.details,
            pixEmv: unifiedResponse.paymentData?.details?.emv || (unifiedResponse.paymentData as any)?.emv,
            finalPricing: unifiedResponse.finalPricing,
            pricing: unifiedResponse.pricing,
          });
          
          // FASE 3: Atualizar lógica para novos tipos de status
          if (unifiedResponse.success === 'pending_3ds') {
            // Backend decidiu iniciar 3DS — o front apenas executa o desafio e devolve o resultado
            const orderId = unifiedResponse.orderId || '';
            // Token de posse da sessão — sem ele, a conclusão do pagamento é recusada (id sequencial não basta).
            const sessionToken = unifiedResponse.sessionToken || '';
            const transactionId = unifiedResponse.transactionId || '';
            const isPagouFlow =
              String(unifiedResponse.gatewayUsed || '').toLowerCase() === 'pagou' ||
              !!unifiedResponse.paymentData?.next_action ||
              !!unifiedResponse.paymentData?.secure_url;

            if (isPagouFlow) {
              const pendingCheckoutResponse: CheckoutResponseDto = {
                order: {
                  nanoId: orderId,
                  gatewayInfos: {
                    gatewayUsed: unifiedResponse.gatewayUsed || 'pagou',
                    gatewayId: transactionId,
                  },
                  totalPrice: unifiedResponse.pricing?.total ?? cartFormData.orderSummary.total,
                  totalDiscounts: unifiedResponse.pricing?.totalDiscounts ?? cartFormData.orderSummary.discountAmount ?? 0,
                  postcode: cartFormData.shippingAddress.zipCode,
                  addressStreet: cartFormData.shippingAddress.street,
                  addressStreetNumber: cartFormData.shippingAddress.number,
                  addressStreetComplement: cartFormData.shippingAddress.complement,
                  addressStreetDistrict: cartFormData.shippingAddress.neighborhood,
                  addressCity: cartFormData.shippingAddress.city,
                  addressState: cartFormData.shippingAddress.state,
                } as any,
                customer: {
                  firstName: cartFormData.userIdentification.fullName.split(' ')[0],
                  lastName: cartFormData.userIdentification.fullName.split(' ').slice(1).join(' '),
                  email: cartFormData.userIdentification.email,
                  documentNumber: cartFormData.paymentInformation.creditCard?.cpf || cartFormData.userIdentification.cpf || '',
                } as any,
                offer: {
                  nanoId: cartFormData.shopifyOffer.nanoId,
                  uuid: cartFormData.shopifyOffer.uuid,
                  cart: cartFormData.shopifyOffer.cart,
                } as any,
                paymentMethod: cartFormData.paymentInformation as any,
                paymentData: unifiedResponse.paymentData,
                paymentStatus: 'pending',
              };

              setOrderResponse(pendingCheckoutResponse);
              setEditingSection(null);
              removeLoading('placeOrder');
              return;
            }

            if (!transactionId) {
              console.error('❌ transactionId não encontrado na resposta do backend');
              errorsContext.addError(
                new CheckoutError('Erro interno: ID da transação não encontrado.', { severity: 'error', code: 'MISSING_TRANSACTION_ID' }),
              );
              removeLoading('placeOrder');
              return;
            }

            const customerData = {
              fullName: cartFormData.userIdentification.fullName, // ✅ Do carrinho
              document: cartFormData.paymentInformation.creditCard?.cpf || cartFormData.userIdentification.cpf || '', // ✅ Do carrinho
              email: cartFormData.userIdentification.email, // ✅ Do carrinho
              phone: cartFormData.userIdentification.phone, // ✅ Do carrinho
              birth: cartFormData.userIdentification.birth || '', // ✅ Do carrinho
            };
            const creditCardData = cartFormData.paymentInformation.creditCard; // ✅ Do carrinho


            const execute3DS = async () => {
              try {
                console.log('🔄 Executando fluxo 3DS...');

                // Função auxiliar: envia o resultado do desafio ao back e interpreta a resposta
                // O front nunca decide o que mostrar — apenas repassa o payload e aguarda o back
                const finalize3DSWithBackend = async (challengePayload: any) => {
                  try {
                    console.log('🔄 Enviando resultado do desafio 3DS ao backend...');
                    const finalizationData = await processAdditionalPayment(orderId, challengePayload, sessionToken);
                    console.log('✅ Resposta do backend após 3DS:', finalizationData);

                    // Backend é a fonte de verdade: success===true ou pending_analysis → avança
                    if (
                      finalizationData.success === true ||
                      finalizationData.success === 'pending_analysis'
                    ) {
                      const checkoutResponse: CheckoutResponseDto = {
                        order: {
                          nanoId: String(finalizationData.orderId ?? ''),
                          gatewayInfos: finalizationData.gatewayUsed ? {
                            gatewayUsed: finalizationData.gatewayUsed,
                            gatewayId: finalizationData.transactionId,
                          } : undefined,
                          totalPrice: finalizationData.pricing?.total ?? cartFormData.orderSummary.total,
                          totalDiscounts: finalizationData.pricing?.totalDiscounts ?? cartFormData.orderSummary.discountAmount ?? 0,
                          postcode: cartFormData.shippingAddress.zipCode,
                          addressStreet: cartFormData.shippingAddress.street,
                          addressStreetNumber: cartFormData.shippingAddress.number,
                          addressStreetComplement: cartFormData.shippingAddress.complement,
                          addressStreetDistrict: cartFormData.shippingAddress.neighborhood,
                          addressCity: cartFormData.shippingAddress.city,
                          addressState: cartFormData.shippingAddress.state,
                        } as any,
                        customer: {
                          firstName: cartFormData.userIdentification.fullName.split(' ')[0],
                          lastName: cartFormData.userIdentification.fullName.split(' ').slice(1).join(' '),
                          email: cartFormData.userIdentification.email,
                          documentNumber: cartFormData.paymentInformation.creditCard?.cpf || cartFormData.userIdentification.cpf || '',
                        } as any,
                        offer: {
                          nanoId: cartFormData.shopifyOffer.nanoId,
                          uuid: cartFormData.shopifyOffer.uuid,
                          cart: cartFormData.shopifyOffer.cart,
                        } as any,
                        paymentMethod: cartFormData.paymentInformation as any,
                        paymentData: finalizationData.paymentData,
                        paymentStatus: finalizationData.success === true ? 'success' : 'pending',
                      } as CheckoutResponseDto;

                      setOrderResponse(checkoutResponse);
                      onCardApproved(checkoutResponse, (finalizationData as any)?.checkoutToken);
                      setEditingSection(null);
                      trackPurchase(checkoutResponse);

                      if (concludeCart) {
                        if (createOrUpdateCartData) {
                          createOrUpdateCartData({
                            fullName: cartFormData.userIdentification.fullName,
                            email: cartFormData.userIdentification.email,
                            phone: cartFormData.userIdentification.phone,
                            document: cartFormData.userIdentification.cpf,
                            address: {
                              city: cartFormData.shippingAddress.city,
                              state: cartFormData.shippingAddress.state,
                              street: cartFormData.shippingAddress.street,
                              postcode: cartFormData.shippingAddress.zipCode,
                              streetNumber: cartFormData.shippingAddress.number,
                              streetDistrict: cartFormData.shippingAddress.neighborhood,
                              streetComplement: cartFormData.shippingAddress.complement,
                            },
                            currentStep: 'PAYMENT',
                          })
                            .then(() => concludeCart())
                            .catch((err) => console.error('❌ Erro ao concluir carrinho (após 3DS):', err));
                        } else {
                          concludeCart().catch((err) => console.error('❌ Erro ao concluir carrinho (após 3DS):', err));
                        }
                      }
                    } else {
                      // Backend retornou falha (pending_payment, card_denied, false, etc.)
                      // A mensagem de erro vem do backend — o front apenas exibe
                      // Token Pagou já foi consumido no 3DS: invalida para a próxima
                      // tentativa gerar token novo (evita token_consumed pós-3DS).
                      window.__pagouResetToken?.();
                      const errorMessage = finalizationData.message || finalizationData.error || 'Erro ao processar pagamento. Tente novamente.';
                      showCheckoutPaymentError(errorMessage);
                      errorsContext.addError(
                        new CheckoutError(errorMessage, {
                          severity: 'error',
                          code: String(finalizationData.success ?? '3DS_ERROR'),
                          showModal: true,
                        }),
                      );
                    }
                  } catch (err) {
                    console.error('❌ Erro ao comunicar com backend após 3DS:', err);
                    window.__pagouResetToken?.();
                    const finalizationErrorMessage = 'Erro ao finalizar pedido após autenticação 3D Secure.';
                    showCheckoutPaymentError(finalizationErrorMessage);
                    errorsContext.addError(
                      new CheckoutError(finalizationErrorMessage, {
                        severity: 'error',
                        code: '3DS_FINALIZATION_ERROR',
                        showModal: true,
                      }),
                    );
                  } finally {
                    removeLoading('placeOrder');
                  }
                };

                await executeFull3DSFlow(
                  transactionId,
                  customerData,
                  creditCardData,
                  cartFormData.shippingAddress,
                  cartFormData.orderSummary.total,
                  async (result: any) => {
                    // onSuccess: desafio concluído → envia payload ao back e aguarda decisão
                    console.log('✅ Desafio 3DS concluído — enviando payload ao backend...');
                    await finalize3DSWithBackend({
                      address: result.address,
                      credit_card: result.credit_card,
                      credentials: result.credentials,
                    });
                  },
                  async (error: any) => {
                    // onError: script 3DS retornou erro → envia ao back para decidir (ex: Life cycle)
                    // O back pode optar por tentar aprovar mesmo assim ou retornar falha
                    console.log('⚠️ Script 3DS retornou erro — enviando ao backend para decisão:', error);
                    await finalize3DSWithBackend({
                      challengeError: true,
                      error: error?.error || error?.message || 'Erro desconhecido no desafio 3DS',
                      code: error?.code,
                    });
                  },
                );
              } catch (error) {
                console.error('❌ Erro ao executar 3DS (pending_3ds):', error);
                window.__pagouResetToken?.();
                showCheckoutPaymentError('Erro inesperado na autenticação 3D Secure.');
                errorsContext.addError(
                  new CheckoutError('Erro inesperado na autenticação 3D Secure.', { severity: 'error', code: '3DS_ERROR', showModal: true }),
                );

                // ✅ Remove loading quando houver erro na execução do 3DS
                removeLoading('placeOrder');
              }
            };

            // ✅ Executa 3DS de forma assíncrona e trata erros
            execute3DS().catch((error) => {
              console.error('❌ Erro na execução do 3DS:', error);
              window.__pagouResetToken?.();
              showCheckoutPaymentError('Erro inesperado na autenticação 3D Secure.');
              errorsContext.addError(
                new CheckoutError('Erro inesperado na autenticação 3D Secure.', { severity: 'error', code: '3DS_ERROR', showModal: true }),
              );
              removeLoading('placeOrder');
            });
            // Loading será removido apenas quando 3DS for finalizado (sucesso ou erro)
            return;
          }

          // Stripe cartão Elements: confirmar no cliente com confirmCardPayment
          // payment_in_process = backend diz "front: mantenha loading, execute confirmação"
          const isStripeCardPending =
            (unifiedResponse.success === 'payment_in_process' || unifiedResponse.success === 'pending_payment') &&
            unifiedResponse.paymentData?.clientSecret &&
            (unifiedResponse.paymentData?.method === 'credit_card' || unifiedResponse.paymentData?.provider === 'stripe');

          if (isStripeCardPending) {
            const clientSecret = unifiedResponse.paymentData.clientSecret;
            const handlers = stripeCardHandlersRef.current;

            if (!handlers) {
              errorsContext.addError(
                new CheckoutError('Erro ao processar pagamento. Recarregue e tente novamente.', {
                  severity: 'error',
                  code: 'STRIPE_ELEMENTS_MISSING',
                }),
              );
              removeLoading('placeOrder');
              return;
            }

            (async () => {
              try {
                const stripe = handlers.getStripe();
                if (!stripe) {
                  errorsContext.addError(new CheckoutError('Stripe não carregou. Tente novamente.'));
                  removeLoading('placeOrder');
                  return;
                }
                const cardElement = handlers.getCardElement();
                if (!cardElement) {
                  errorsContext.addError(new CheckoutError('Dados do cartão não encontrados. Preencha e tente novamente.'));
                  removeLoading('placeOrder');
                  return;
                }

                const { paymentIntent, error } = await (stripe as { confirmCardPayment: (secret: string, opts?: any) => Promise<{ paymentIntent?: { id: string }; error?: any }> }).confirmCardPayment(clientSecret, {
                  payment_method: {
                    card: cardElement,
                    billing_details: {
                      name: cartFormData.paymentInformation.creditCard?.cardholderName || cartFormData.userIdentification.fullName,
                    },
                  },
                });

                if (error) {
                  let errorMsg = 'Erro no pagamento. Tente novamente.';
                  try {
                    const finalizeResult = await stripeElementsFinalize({
                      cartCode,
                      sessionId: Number(unifiedResponse.orderId),
                      result: 'error',
                      transactionId: paymentIntent?.id || unifiedResponse.transactionId,
                      stripeError: { code: error.code, decline_code: (error as any).decline_code, message: error.message },
                    });
                    errorMsg = finalizeResult.error || errorMsg;
                  } catch {
                    errorMsg = error.message || errorMsg;
                  }
                  errorsContext.addError(new CheckoutError(errorMsg, { severity: 'error', code: 'CARD_DENIED', showModal: true }));
                  if (typeof window !== 'undefined' && (window as any).setCheckoutPaymentErrorMessage) {
                    (window as any).setCheckoutPaymentErrorMessage(errorMsg);
                  }
                  removeLoading('placeOrder');
                  return;
                }

                const finalizeResult = await stripeElementsFinalize({
                  cartCode,
                  sessionId: Number(unifiedResponse.orderId),
                  result: 'success',
                  transactionId: paymentIntent?.id || unifiedResponse.transactionId,
                });

                if (!finalizeResult.success) {
                  const errorMsg = finalizeResult.error || 'Erro ao finalizar pagamento.';
                  errorsContext.addError(new CheckoutError(errorMsg, { severity: 'error', code: 'CARD_DENIED', showModal: true }));
                  if (typeof window !== 'undefined' && (window as any).setCheckoutPaymentErrorMessage) {
                    (window as any).setCheckoutPaymentErrorMessage(errorMsg);
                  }
                  removeLoading('placeOrder');
                  return;
                }

                const orderNanoId = String(finalizeResult.orderId ?? unifiedResponse.orderId ?? '');
                const checkoutResponse: CheckoutResponseDto = {
                  order: {
                    nanoId: orderNanoId,
                    gatewayInfos: {
                      gatewayUsed: 'stripe',
                      gatewayId: unifiedResponse.transactionId,
                    },
                    totalPrice: unifiedResponse.pricing?.total ?? cartFormData.orderSummary.total,
                    totalDiscounts: unifiedResponse.pricing?.totalDiscounts ?? cartFormData.orderSummary.discountAmount ?? 0,
                    postcode: cartFormData.shippingAddress.zipCode,
                    addressStreet: cartFormData.shippingAddress.street,
                    addressStreetNumber: cartFormData.shippingAddress.number,
                    addressStreetComplement: cartFormData.shippingAddress.complement,
                    addressStreetDistrict: cartFormData.shippingAddress.neighborhood,
                    addressCity: cartFormData.shippingAddress.city,
                    addressState: cartFormData.shippingAddress.state,
                  } as any,
                  customer: {
                    firstName: cartFormData.userIdentification.fullName.split(' ')[0],
                    lastName: cartFormData.userIdentification.fullName.split(' ').slice(1).join(' '),
                    email: cartFormData.userIdentification.email,
                    documentNumber: cartFormData.paymentInformation.creditCard?.cpf || cartFormData.userIdentification.cpf || '',
                  } as any,
                  offer: {
                    nanoId: cartFormData.shopifyOffer.nanoId,
                    uuid: cartFormData.shopifyOffer.uuid,
                    cart: cartFormData.shopifyOffer.cart,
                  } as any,
                  paymentMethod: cartFormData.paymentInformation as any,
                  paymentData: unifiedResponse.paymentData,
                  paymentStatus: 'success',
                };

                setOrderResponse(checkoutResponse);
                onCardApproved(checkoutResponse, (finalizeResult as any)?.checkoutToken);
                setEditingSection(null);
                trackPurchase(checkoutResponse);

                if (concludeCart && createOrUpdateCartData) {
                  await createOrUpdateCartData({
                    fullName: cartFormData.userIdentification.fullName,
                    email: cartFormData.userIdentification.email,
                    phone: cartFormData.userIdentification.phone,
                    document: cartFormData.userIdentification.cpf,
                    address: {
                      city: cartFormData.shippingAddress.city,
                      state: cartFormData.shippingAddress.state,
                      street: cartFormData.shippingAddress.street,
                      postcode: cartFormData.shippingAddress.zipCode,
                      streetNumber: cartFormData.shippingAddress.number,
                      streetDistrict: cartFormData.shippingAddress.neighborhood,
                      streetComplement: cartFormData.shippingAddress.complement,
                    },
                    currentStep: 'PAYMENT',
                  });
                  await concludeCart();
                }
              } catch (err: any) {
                errorsContext.addError(
                  new CheckoutError(err?.message || 'Erro ao confirmar pagamento.', { severity: 'error', showModal: true }),
                );
              } finally {
                removeLoading('placeOrder');
              }
            })();
            return;
          }

          // payment_in_process sem clientSecret = estrutura incompleta, não deveria ocorrer
          if (unifiedResponse.success === 'payment_in_process') {
            errorsContext.addError(
              new CheckoutError('Erro: fluxo de pagamento incompleto. Recarregue e tente novamente.', {
                severity: 'error',
                code: 'PAYMENT_IN_PROCESS_INVALID',
              }),
            );
            removeLoading('placeOrder');
            return;
          }

          // Tratar outros tipos de status (não incluir payment_in_process — loading continua até resultado final)
          if (
            unifiedResponse.success === 'pending_analysis' ||
            unifiedResponse.success === 'pending_payment' ||
            unifiedResponse.success === true
          ) {
            console.log(`✅ Backend retornou success: ${unifiedResponse.success} - redirecionando para sucesso`);

            // Converte resposta unificada para formato compatível
            const checkoutResponse: CheckoutResponseDto = {
              order: {
                nanoId: String(unifiedResponse.orderId ?? ''),
                gatewayInfos: unifiedResponse.gatewayUsed ? {
                  gatewayUsed: unifiedResponse.gatewayUsed,
                  gatewayId: unifiedResponse.transactionId,
                } : undefined,
                totalPrice: unifiedResponse.pricing?.total ?? cartFormData.orderSummary.total, // ✅ Do carrinho
                totalDiscounts: unifiedResponse.pricing?.totalDiscounts ?? cartFormData.orderSummary.discountAmount ?? 0, // ✅ Do carrinho
                postcode: cartFormData.shippingAddress.zipCode, // ✅ Do carrinho
                addressStreet: cartFormData.shippingAddress.street, // ✅ Do carrinho
                addressStreetNumber: cartFormData.shippingAddress.number, // ✅ Do carrinho
                addressStreetComplement: cartFormData.shippingAddress.complement, // ✅ Do carrinho
                addressStreetDistrict: cartFormData.shippingAddress.neighborhood, // ✅ Do carrinho
                addressCity: cartFormData.shippingAddress.city, // ✅ Do carrinho
                addressState: cartFormData.shippingAddress.state, // ✅ Do carrinho
              } as any,
              customer: {
                firstName: cartFormData.userIdentification.fullName.split(' ')[0], // ✅ Do carrinho
                lastName: cartFormData.userIdentification.fullName.split(' ').slice(1).join(' '), // ✅ Do carrinho
                email: cartFormData.userIdentification.email, // ✅ Do carrinho
                documentNumber:
                  cartFormData.paymentInformation.paymentMethod === 'credit_card'
                    ? cartFormData.paymentInformation.creditCard?.cpf || ''
                    : cartFormData.paymentInformation.paymentMethod === 'pix'
                    ? cartFormData.paymentInformation.pix?.cpf || ''
                    : cartFormData.paymentInformation.boleto?.cpf || '', // ✅ Do carrinho
              } as any,
              offer: {
                nanoId: cartFormData.shopifyOffer.nanoId, // ✅ Do carrinho
                uuid: cartFormData.shopifyOffer.uuid, // ✅ Do carrinho
                cart: cartFormData.shopifyOffer.cart, // ✅ Do carrinho
              } as any,
              paymentMethod: cartFormData.paymentInformation as any, // ✅ Do carrinho
              paymentData: unifiedResponse.paymentData,
              paymentStatus: unifiedResponse.success === true ? 'success' : 'pending',
            };

            setOrderResponse(checkoutResponse);
            onCardApproved(checkoutResponse, unifiedResponse.checkoutToken);
            setEditingSection(null);
            trackPurchase(checkoutResponse);

            // ✅ Marca o carrinho como concluído quando o checkout for bem-sucedido
            if (concludeCart) {
              // ✅ Primeiro atualiza o carrinho com os dados atuais antes de concluí-lo
              if (createOrUpdateCartData) {
                console.log('🔄 Atualizando carrinho com dados finais antes de concluí-lo...');

                const finalCartData = {
                  fullName: cartFormData.userIdentification.fullName,
                  email: cartFormData.userIdentification.email,
                  phone: cartFormData.userIdentification.phone,
                  document: cartFormData.userIdentification.cpf,
                  address: {
                    city: cartFormData.shippingAddress.city,
                    state: cartFormData.shippingAddress.state,
                    street: cartFormData.shippingAddress.street,
                    postcode: cartFormData.shippingAddress.zipCode,
                    streetNumber: cartFormData.shippingAddress.number,
                    streetDistrict: cartFormData.shippingAddress.neighborhood,
                    streetComplement: cartFormData.shippingAddress.complement,
                  },
                  currentStep: 'PAYMENT',
                };

                createOrUpdateCartData(finalCartData)
                  .then(() => {
                    console.log('✅ Carrinho atualizado com dados finais, agora marcando como concluído...');
                    // ✅ Agora marca como concluído
                    return concludeCart();
                  })
                  .then(() => {
                    console.log('✅ Carrinho marcado como concluído com sucesso');
                  })
                  .catch((error) => {
                    console.error('❌ Erro ao marcar carrinho como concluído:', error);
                  });
              } else {
                // ✅ Fallback: tenta concluir diretamente se não conseguir atualizar
                concludeCart()
                  .then(() => {
                    console.log('✅ Carrinho marcado como concluído com sucesso (sem atualização)');
                  })
                  .catch((error) => {
                    console.error('❌ Erro ao marcar carrinho como concluído:', error);
                  });
              }
            }

            removeLoading('placeOrder');
            return;
          }

          // Tratar card_denied
          if (unifiedResponse.success === 'card_denied') {
            // Token Pagou é de uso único — invalida o token consumido para que a
            // próxima tentativa gere um novo (evita token_consumed).
            window.__pagouResetToken?.();
            console.log('❌ Backend retornou success: card_denied - cartão recusado');

            // Usa a mensagem de recusa do backend (motivo da operadora) como fonte de
            // verdade; cai num texto genérico só se o backend não mandar nada.
            const deniedMessage =
              unifiedResponse.message ||
              unifiedResponse.error ||
              'Transação não autorizada pela operadora do cartão, confira seus dados ou tente um novo cartão';

            // Exibe no overlay (único renderizador de erro visível) — sem isso o
            // addError abaixo é no-op visual e o cliente não vê nada (P2-3).
            showCheckoutPaymentError(deniedMessage);

            errorsContext.addError(
              new CheckoutError(deniedMessage, {
                severity: 'error',
                code: 'CARD_DENIED',
                showModal: true,
              }),
            );

            removeLoading('placeOrder');
            return;
          }

          // Verifica se houve erro real no backend
          if (unifiedResponse.success === false) {
            // Invalida o token consumido nesta tentativa (uso único na Pagou).
            window.__pagouResetToken?.();
            console.log('❌ Backend retornou success: false - tratando como erro');

            // Para TODOS os métodos de pagamento, mantém na etapa de pagamento e mostra modal
            const errorMessage = unifiedResponse.error || 'Ocorreu um erro inesperado ao finalizar o pedido.';

            console.log('🔄 Erro de pagamento - mantendo na etapa de pagamento');

            // Usa a mensagem do backend como fonte de verdade (já formatada)
            const specificErrorMessage = errorMessage;

            // Exibe no overlay e mantém também o alerta para consistência visual
            showCheckoutPaymentError(specificErrorMessage);

            errorsContext.addError(new CheckoutError(specificErrorMessage, { severity: 'error', code: '400', showModal: true }));

            removeLoading('placeOrder');
            return;
          }
        })
        .catch((error) => {
          // ✅ LOG BACKEND: Erro ao realizar checkout unificado
          if (typeof window !== 'undefined' && (window as any).logToBackend) {
            (window as any).logToBackend(`Erro ao realizar checkout unificado: ${error.message || 'Erro desconhecido'}`, 'error');
          }
          
          console.error('Erro ao realizar checkout unificado:', error);

          // Falha na requisição: o token pode ter sido consumido/expirado no backend.
          // Invalida para forçar novo token na próxima tentativa.
          window.__pagouResetToken?.();

          // Para TODOS os métodos de pagamento, mantém na etapa de pagamento e mostra modal
          const errorMessage = error instanceof CheckoutError ? error.message : 'Ocorreu um erro inesperado ao finalizar o pedido.';

          console.log('🔄 Erro de pagamento - mantendo na etapa de pagamento');

          // Mensagens específicas por método de pagamento
          let specificErrorMessage = errorMessage;
          if (cartFormData.paymentInformation.paymentMethod === 'credit_card') {
            // ✅ Do carrinho
            specificErrorMessage = 'Transação não autorizada pela operadora do cartão, confira seus dados ou tente um novo cartão';
          } else if (cartFormData.paymentInformation.paymentMethod === 'pix') {
            // ✅ Do carrinho
            specificErrorMessage = 'Erro ao gerar PIX, verifique os dados e tente novamente';
          } else if (cartFormData.paymentInformation.paymentMethod === 'boleto') {
            // ✅ Do carrinho
            specificErrorMessage = 'Erro ao gerar boleto, verifique os dados e tente novamente';
          }

          showCheckoutPaymentError(specificErrorMessage);

          errorsContext.addError(new CheckoutError(specificErrorMessage, { severity: 'error', code: '400', showModal: true }));

          // NÃO limpa o orderResponse para manter o usuário na etapa de pagamento
          // setOrderResponse(null); // Comentado para evitar limpar o frontend
          removeLoading('placeOrder'); // Para o spinner imediatamente
        });

      // ✅ CATCH para tratar erros da busca do carrinho
    } catch (cartError) {
      console.error('Erro ao buscar dados do carrinho:', cartError);

      const errorMessage = cartError instanceof CheckoutError ? cartError.message : 'Erro ao buscar dados do carrinho. Tente novamente.';

      errorsContext.addError(
        new CheckoutError(errorMessage, {
          severity: 'error',
          code: 'CART_FETCH_ERROR',
          showModal: true,
        }),
      );

      removeLoading('placeOrder');
    }
  }, [
    formData, 
    steps, 
    errorsContext, 
    addLoading, 
    removeLoading, 
    setOrderResponse, 
    setEditingSection, 
    trackPurchase, 
    capturePaymentDataFromForm, 
    executeFull3DSFlow, 
    createOrUpdateCartData,
    concludeCart,
    shopifyOffer,
    onCardApproved,
  ]);

  // ✅ Função genérica para quando uma etapa é completada
  const onStepComplete = useCallback(
    (stepId: string, updatedData: Partial<FormData>, currentFormData: FormData) => {
      // ✅ Evita duplicação de chamadas
      const stepKey = `${stepId}-${JSON.stringify(updatedData)}`;
      if (lastStepCompleteRef.current === stepKey) {
        return;
      }

      lastStepCompleteRef.current = stepKey;

      // ✅ Determina qual será a próxima etapa baseada na etapa atual
      let nextStep = 'IDENTIFICATION';
      if (stepId === 'IDENTIFICATION') {
        nextStep = 'SHIPPING';
      } else if (stepId === 'SHIPPING') {
        nextStep = 'PAYMENT';
      } else if (stepId === 'PAYMENT') {
        nextStep = 'PAYMENT'; // Permanece em PAYMENT até finalizar
      }

      // Atualiza cart em TODAS as etapas com todas as informações disponíveis
      if (createOrUpdateCartData) {
        // Reúne todas as informações disponíveis do formData atualizado
        const allUserData = {
          // Dados de identificação (se disponíveis)
          fullName: currentFormData.userIdentification?.fullName || '',
          email: currentFormData.userIdentification?.email || '',
          phone: currentFormData.userIdentification?.phone || '',
          document: currentFormData.userIdentification?.cpf || '',
          birth: currentFormData.userIdentification?.birth || '', // ✅ Adicionado campo birth

          // Dados de endereço (se disponíveis)
          address: {
            city: currentFormData.shippingAddress?.city || '',    
            state: currentFormData.shippingAddress?.state || '',
            street: currentFormData.shippingAddress?.street || '',
            postcode: currentFormData.shippingAddress?.zipCode || '',
            streetNumber: currentFormData.shippingAddress?.number || '',
            streetDistrict: currentFormData.shippingAddress?.neighborhood || '',
            streetComplement: currentFormData.shippingAddress?.complement || '',
          },

          // ✅ Adiciona o currentStep para indicar onde o usuário parou
          currentStep: nextStep,
        };

        // Atualiza sempre — o cart existe desde a primeira interação e o backend
        // faz merge sem apagar dados já capturados. O gate de contato pra disparo
        // de recuperação vive no backend (markAsAbandoned), não aqui.
        createOrUpdateCartData(allUserData).catch(console.error);
      }

      // Shipping - Analytics
      if ((stepId === 'SHIPPING' || stepId === 'shipping') && updatedData.shippingAddress) {
        console.log('📊 Disparando analytics de shipping...');
        trackAddShippingInfo();
      }

      // Payment analytics: disparado em handlePaymentMethodChange (quando método é selecionado),
      // não aqui (que só roda ao clicar em Finalizar — evento duplicado e tardio)

      // Futuro: Adicionar mais lógicas aqui
      // - Tracking de conversão
      // - Integração com CRM
      // - Notificações
      // - etc.
    },
    [createOrUpdateCartData, trackAddShippingInfo, trackAddPaymentInfo],
  );

  const handleSaveSection = useCallback(
    (updatedData: Partial<FormData>) => {
      // Identifica qual etapa foi completada ANTES de atualizar o formData
      let completedStepId = '';
      if (updatedData.userIdentification) completedStepId = 'IDENTIFICATION';
      else if (updatedData.shippingAddress) completedStepId = 'SHIPPING';
      else if (updatedData.paymentInformation) completedStepId = 'PAYMENT';

      // Atualiza os dados primeiro
      setFormData((prev) => {
        const newFormData = { ...prev, ...updatedData };

        // Chama função genérica de conclusão de etapa COM os dados atualizados
        if (completedStepId) {
          onStepComplete(completedStepId, updatedData, newFormData);
        }

        return newFormData;
      });

      // ✅ Navegação corrigida - usa os IDs corretos das etapas
      if (completedStepId === 'IDENTIFICATION') {
        setEditingSection('SHIPPING');
      } else if (completedStepId === 'SHIPPING') {
        setEditingSection('PAYMENT');
      }
      // PAYMENT não navega automaticamente - usuário precisa finalizar o pedido
    },
    [setFormData, setEditingSection, onStepComplete],
  );

  /**
   * Toggle/atualiza a lista de order bumps aceitos.
   * Fluxo: PATCH no carrinho → refetch preços (com cartCode) → backend soma após descontos.
   * Loading de tela cheia via chave 'orderBumpChange'.
   */
  const handleOrderBumpsChange = useCallback(
    async (nextAcceptedNanoIds: string[]) => {
      const cartCode = getCartCode();
      console.log('[ORDERBUMP] toggle', {
        cartCode,
        next: nextAcceptedNanoIds,
      });
      if (!cartCode) {
        errorsContext.addError(
          new CheckoutError('Não foi possível atualizar o pedido. Recarregue a página.'),
          { severity: 'warning' },
        );
        return;
      }

      try {
        addLoading('orderBumpChange');

        console.log('[ORDERBUMP] PATCH cart start', cartCode);
        const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/carts/${cartCode}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ acceptedOrderBumps: nextAcceptedNanoIds }),
        });
        if (!response.ok) {
          throw new CheckoutError('Erro ao atualizar oferta no carrinho.', {
            status: response.status,
          });
        }
        console.log('[ORDERBUMP] PATCH cart ok');

        await updatePricesFromBackend('orderBumpChange');
        console.log('[ORDERBUMP] refetch done');
      } catch (error) {
        console.error('[ORDERBUMP] toggle error', error);
        errorsContext.addError(new CheckoutError('Erro ao atualizar oferta.'), { severity: 'warning' });
      } finally {
        removeLoading('orderBumpChange');
      }
    },
    [addLoading, removeLoading, updatePricesFromBackend, errorsContext],
  );

  /**
   * Cliente aceitou o upsell ("eu quero"). Cobra no mesmo cartão (token `pgpm_`).
   * Em sucesso → fecha popup → redirect. Em falha → retorna mensagem curta
   * (o popup exibe e em seguida o fluxo segue para o thank-you — decisão do dono).
   */
  const handleUpsellAccept = useCallback(async (): Promise<string | null> => {
    const state = upsellState;
    if (!state) return null;

    try {
      const result = await chargeUpsell({
        checkoutToken: state.checkoutToken ?? '',
        orderId: state.orderId,
        upsellNanoId: state.upsell.nanoId,
        reusableToken: state.reusableToken,
      });

      if (result.success) {
        setUpsellState(null);
        redirectToThankYou(state.checkoutToken);
        return null;
      }

      // Falha de cobrança: mostra mensagem e segue para o thank-you.
      const message = result.error || 'Não foi possível adicionar a oferta. Seguindo para a confirmação.';
      setUpsellState(null);
      redirectToThankYou(state.checkoutToken);
      return message;
    } catch {
      setUpsellState(null);
      redirectToThankYou(state.checkoutToken);
      return 'Não foi possível adicionar a oferta. Seguindo para a confirmação.';
    }
  }, [upsellState, redirectToThankYou]);

  /** Cliente recusou o upsell ou pressionou Esc → fecha popup → redirect. */
  const handleUpsellDecline = useCallback(() => {
    const state = upsellState;
    setUpsellState(null);
    redirectToThankYou(state?.checkoutToken);
  }, [upsellState, redirectToThankYou]);

  return {
    handleCouponApply,
    handlePaymentMethodChange,
    handleSaveSection,
    handlePlaceOrder,
    handleOrderBumpsChange,
    updatePricesFromBackend,
    onStepComplete,
    onCardElementReady,
    onReusableToken,
    upsellState,
    handleUpsellAccept,
    handleUpsellDecline,
  };
};
